import { useState, useEffect } from 'react';
import { Menu, X, Globe, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-cream-100 shadow-md py-3' : 'bg-transparent py-5'}`}>
      <div className="max-w-[120rem] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-4">
            <img src="/assets/logo_v2.webp" alt="Icone L'art du Ménage" className="h-16 md:h-20 w-auto object-contain drop-shadow-md" width="400" height="150" />
            <div className="flex flex-col">
              <div className={`font-serif text-xl md:text-2xl tracking-widest font-bold ${isScrolled ? 'text-black-deep' : 'text-gold-500'}`}>
                L'ART DU MÉNAGE
              </div>
              <div className={`font-script text-lg md:text-xl mt-1 ${isScrolled ? 'text-gold-600' : 'text-white'}`}>
                par Johanna
              </div>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden xl:flex space-x-6 xl:space-x-8 items-center">
            <Link to="/methode" className={`text-sm uppercase tracking-wider hover:text-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${isScrolled ? 'text-black-deep' : 'text-white'}`}>{t('navbar.method')}</Link>
            <Link to="/forfaits" className={`text-sm uppercase tracking-wider hover:text-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${isScrolled ? 'text-black-deep' : 'text-white'}`}>{t('navbar.packages')}</Link>
            <Link to="/faq" className={`text-sm uppercase tracking-wider hover:text-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${isScrolled ? 'text-black-deep' : 'text-white'}`}>FAQ</Link>
            <Link to="/realisations" className={`text-sm uppercase tracking-wider hover:text-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${isScrolled ? 'text-black-deep' : 'text-white'}`}>{t('navbar.portfolio')}</Link>
            
            {/* Lang Switcher */}
            <button 
              onClick={() => i18n.changeLanguage(i18n.language.startsWith('fr') ? 'en' : 'fr')}
              className={`flex items-center space-x-1 text-sm uppercase tracking-wider hover:text-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${isScrolled ? 'text-black-deep' : 'text-white'}`}
              aria-label="Toggle language between French and English"
            >
              <Globe size={16} />
              <span>{i18n.language.startsWith('fr') ? 'EN' : 'FR'}</span>
            </button>

            <Link 
              to="/login" 
              className={`p-2 border rounded-full hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${isScrolled ? 'border-gold-500 text-gold-600 hover:bg-gold-50' : 'border-white text-white hover:bg-white/10'}`}
              aria-label={t('navbar.clientArea')}
              title={t('navbar.clientArea')}
            >
              <User size={18} />
            </Link>
            <Link to="/audit" className="px-8 py-3 bg-gradient-to-r from-gold-400 to-gold-600 text-white text-sm uppercase tracking-widest rounded-full shadow-[0_8px_20px_rgba(201,152,85,0.4)] hover:shadow-[0_12px_30px_rgba(201,152,85,0.6)] hover:-translate-y-1 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white inline-block">
              {t('navbar.audit')}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="xl:hidden flex items-center space-x-4">
            <button 
              onClick={() => i18n.changeLanguage(i18n.language.startsWith('fr') ? 'en' : 'fr')}
              className={`hover:text-gold-500 transition-colors ${isScrolled ? 'text-black-deep' : 'text-white'}`}
              aria-label="Toggle language"
            >
              {i18n.language.startsWith('fr') ? 'EN' : 'FR'}
            </button>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              className={`hover:text-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${isScrolled ? 'text-black-deep' : 'text-white'}`}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-cream-100 absolute w-full shadow-lg border-t border-gold-200">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link onClick={() => setMobileMenuOpen(false)} to="/methode" className="block px-3 py-2 text-black-deep hover:text-gold-600 font-serif">{t('navbar.method')}</Link>
            <Link onClick={() => setMobileMenuOpen(false)} to="/forfaits" className="block px-3 py-2 text-black-deep hover:text-gold-600 font-serif">{t('navbar.packages')}</Link>
            <Link onClick={() => setMobileMenuOpen(false)} to="/faq" className="block px-3 py-2 text-black-deep hover:text-gold-600 font-serif">FAQ</Link>
            <Link onClick={() => setMobileMenuOpen(false)} to="/realisations" className="block px-3 py-2 text-black-deep hover:text-gold-600 font-serif">{t('navbar.portfolio')}</Link>
            <div className="mt-4 flex flex-col gap-2 px-3 pb-4">
              <Link onClick={() => setMobileMenuOpen(false)} to="/login" className="w-full px-6 py-3 border border-gold-500 text-gold-600 text-sm uppercase tracking-wider text-center block">
                {t('navbar.clientArea')}
              </Link>
              <Link onClick={() => setMobileMenuOpen(false)} to="/audit" className="w-full px-6 py-3 bg-gradient-to-r from-gold-400 to-gold-600 text-white text-sm uppercase tracking-wider text-center block shadow-md">
                {t('navbar.audit')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
